<template>
  <v-container class="fill-height registration" fluid>
    <v-row>
      <v-col cols="12" sm="6" class="text-center">
        <img
          :src="images.jaceyUwill"
          style="margin-top:44px;"
          alt="Uwill"
          :style="
            $vuetify.display.smAndUp ? 'max-width:560px;' : 'max-width:300px;'
          "
        />
      </v-col>
      <v-col v-if="step === 1" cols="12" sm="5" order="first" order-sm="last">
        <v-row style="margin-top: 5%;">
          <h1 class="section-title-large pb-3">
            Therapy is not <br/> one size fits all.
          </h1>
          <div class="mt-2 mb-4" style="text-align: left; font-size: 14px;">
            Often it can take time to find the therapist you click with, the tools that work for you outside of session, and accomplish your mental health and wellness goals. <br/><br/>
            To tailor this process to you, there are a couple of quick, yet important questions:
          </div>
          <v-col cols="12" class="ml-0 pl-0 student-registration">
            <span class="field-title">What challenge is bringing you to Uwill?</span>
            <v-select
              :key="focusAreasKey"
              id="profile-focus-areas"
              v-model="focusAreas"
              :items="focus_area_choices"
              :menu-props="{ maxHeight: '400' }"
              :label="focusAreas?.length > 0 ? '' : 'Start with one, and include up to three if several feel important'"
              aria-label="Focus Areas"
              persistent-hint
              multiple
              prepend-icon="fa-light fa-bullseye-pointer"
              menu-icon="fa-solid fa-chevron-down"
              @update:model-value="updateFocusAreas"
            />
            <span class="pt-16 field-title">How can we best contact you and someone you trust?</span>
            <profile-location
              v-model:addressOneValidated="address1"
              v-model:addressTwoValidated="address2"
              v-model:cityValidated="city"
              v-model:zipcodeValidated="zipcode"
              v-model:countryValidated="country"
              v-model:stateValidated="state"
              :is-student="isStudent"
            />
            <v-row v-if="!$methods.isIndependentUser() && require_unique_identifier">
              <v-col cols="12" :class="isStudent ? 'py-0' : ''">
                <v-text-field
                  id="profile-unique-identifier"
                  v-model="uniqueIdentifier"
                  :label="$methods.getStudentWord(true) + ' ID'"
                  :aria-label="$methods.getStudentWord(true) + ' ID'"
                  name="uniqueIdentifier"
                  prepend-icon="fa-light fa-id-badge"
                  type="text"
                  required
                  :error-messages="uniqueIdentifierErrorMessages"
                  @update:modelValue="delayTouch(v$.uniqueIdentifier)"
                />
              </v-col>
            </v-row>
            <v-row v-if="!userBirthdate">
              <v-col cols="12" class="mt-3">
                <v-text-field
                  id="profile-birth-date"
                  v-model="birthDate"
                  label="Birth Date"
                  aria-label="Birth Date"
                  prepend-icon="fa-light fa-cake-candles"
                  type="text"
                  maxLength="10"
                  :required="!isContractor"
                  :rules="[birthDateAndGraduationRule(true)]"
                  :hint="isContractor ? '' : 'Required. Example: 2000-01-15.'"
                  persistent-hint
                  :error-messages="birthDateErrorMessages"
                  autocomplete="birth date"
                  @update:model-value="updateBirthDate(birthDate)"
                  @blur="v$.birthDate.$touch()"
                />
              </v-col>
            </v-row>
            <profile-contact
              v-model:contact1NameValidated="contact1Name"
              v-model:contact1TelephoneValidated="contact1Telephone"
              :is-provider="false"
              :is-student="isStudent"
              :user-telephone="telephone"
              @update:contact1-name="contact1Name = $event"
            />
          </v-col>
        </v-row>
        <div class="text-center mt-4 mb-4">
          <v-btn
            id="student-next-step"
            class="uwill-primary-button padded shadowed mt-4"
            style="width:40%; height:100%"
            @click="basicFormValid ? step++ : finish(1)"
          >
            Complete Profile
          </v-btn>
        </div>
      </v-col>
      <v-col v-if="step === 2" cols="12" sm="5" order="first" order-sm="last">
        <v-row style="margin-top: 5%;">
          <h1 class="section-title-large pb-3">
            It’s about you.
          </h1>
          <div class="mt-2 mb-4" style="text-align: left; font-size: 14px;">
            Although this information is optional, therapists often find this information really helpful to learn what makes you, uniquely you.
          </div>
          <v-col cols="12" class="ml-0 pl-0 student-registration">
            <span class="field-title">Your personal touch</span>
            <v-text-field
              id="profile-preferred-name"
              v-model="preferredName"
              label="A Preferred Name"
              aria-label="A Preferred Name"
              prepend-icon="fa-light fa-file-signature"
              type="text"
              :error-messages="preferredNameErrorMessages"
            />
            <v-select
              id="profile-gender"
              v-model="gender"
              :items="gender_choices"
              label="Gender"
              aria-label="Gender"
              value:
              hide-details
              prepend-icon="fa-light fa-mars-and-venus"
              menu-icon="fa-solid fa-chevron-down"
            />
            <v-select
              v-if="!$store.state.user.conservativeExclusions"
              id="profile-pronouns"
              class="mt-6"
              v-model="pronouns"
              :items="pronoun_choices"
              item-value="value"
              item-title="text"
              label="Preferred Pronouns"
              aria-label="Preferred Pronouns"
              prepend-icon="fa-light fa-mars-and-venus"
              menu-icon="fa-solid fa-chevron-down"
            />
            <v-select
              id="profile-ethnicity"
              v-model="ethnicity"
              :items="ethnicity_choices"
              item-value="value"
              item-title="text"
              label="Ethnicity"
              aria-label="Ethnicity"
              multiple
              prepend-icon="fa-light fa-earth-americas"
              menu-icon="fa-solid fa-chevron-down"
            />
            <span class="mt-6 field-title">Reasons for choosing Uwill</span>
            <v-select
              id="profile-reason"
              v-model="reasons"
              :items="reason_choices"
              item-value="value"
              item-title="text"
              :label="reasons ? '' : 'Choose what fits you best'"
              aria-label="Reasons for choosing Uwill"
              multiple
              prepend-icon="fa-light fa-ballot-check"
            />
            <span class="mt-6 field-title">Are there any communities or groups you are part of that are important to you?</span>
            <v-select
              v-if="communities_groups_options && communities_groups_options.length > 0"
              id="profile-communities"
              v-model="communitiesGroups"
              :items="communities_groups_options"
              item-value="value"
              item-title="text"
              :label="communitiesGroups.length > 0 ? '' : 'Choose what fits you best'"
              aria-label="Are there any communities or groups you are part of that are important to you?"
              multiple
              prepend-icon="fa-light fa-user"
              menu-icon="fa-solid fa-chevron-down"
            />
          </v-col>
        </v-row>
        <div class="text-center mt-4 mb-4">
          <v-btn
            id="student-next-step"
            class="uwill-primary-button padded shadowed mt-4"
            style="width:40%; height:100%"
            @click="finish()"
          >
            Add to my profile
          </v-btn>
        </div>
        <v-row
          style="justify-content: center;"
          class="ada-register-continue"
        >
          <v-col cols="12" sm="6" style="margin:auto 0; text-align:center;">
            <a
              class="go-back"
              role="button"
              href="javascript: void(0)"
              @click="step--"
            >
              Go Back
            </a>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
// import Profile from "@/components/Profile.vue";
import useVuelidate from "@vuelidate/core";
import validationMixin from "vuelidate";
import { ref } from "vue";

import { uwillProfileWrapperMixin } from "./profile/profile-wrapper.js";
import { uwillValidatorMixin } from "../components/form/uwillValidatorMixin.js";
import uwillProfileValidationsMixin from "../components/form/uwillProfileValidationsMixin";
import ProfileLocation from "@/components/profile/Location.vue";
import ProfileContact from "@/components/profile/Contact.vue";

const touchMap = new WeakMap();

export default {
  name: "CompleteProfile",
  components: {
    ProfileLocation,
    ProfileContact
  },
  mixins: [uwillProfileWrapperMixin, validationMixin, uwillValidatorMixin, uwillProfileValidationsMixin],
  props: ["userTelephone", "userBirthdate", "isStudent"],
  emits: ["organizations-update", "graduationDateValidated", "birthDateValidated", "uniqueIdentifierValidated"],
  setup() {
    // Fixes a bug where component doesn't render if the checkbox itself is selected
    const focusAreasKey = ref(0);
    const forceRerenderFocusAreas = () => {
      focusAreasKey.value++;
    };

    return {
      focusAreasKey,
      forceRerenderFocusAreas,
      v$: useVuelidate()
    };
  },
  data: () => ({
    confirmed: false,
    activePicker: null,
    birthDate: null,
    employer: false,
    gender: "",
    pronouns: "No Preference",
    telephone: "",
    ethnicity: [],
    ethnicity_choices: [],
    reasons: null,
    focusAreas: [],
    about: "",
    uniqueIdentifier: "",
    require_unique_identifier: false,
    graduationDate: null,
    skip_graduation_date: false,
    requiredFields: [
      "address1",
      "city",
      "country",
      "state",
      "zipcode",
      "contact1Telephone",
      "contact1Name"
    ],
    images: {
      jaceyUwill: require("@/assets/images/jacey_uwill.png")
    },
    step: 1,
    // This variable does not work and it causing loading issues for some reason: communities_groups_choices
    communities_groups_options: [],
    communitiesGroups: []
  }),
  computed: {
    gender_choices() {
      return this.$store.state.user.conservativeExclusions
        ? ["Female", "Male", "No Preference"]
        : ["Female", "Male", "Non-Binary", "No Preference"];
    },
    preferredNameErrorMessages() {
      this.preferredName;
      return this.maxLengthErrors("preferredName");
    },
    aboutErrorMessages() {
      this.about;
      return this.maxLengthErrors("about");
    },
    basicFormProblems() {
      let problems = [];
      for (let i = 0; i < this.requiredFields.length; i++) {
        let field = this.requiredFields[i];
        let value = this[field];

        // Required
        if (value == null || value === "") {
          if (
            field == "state" &&
            !["United States", "Canada"].includes(this.country)
          ) {
            // Noop - State is not required for countries outside the US & Canada
          } else {
            if (field == "state" && this.country == "Canada") {
              problems.push("Province");
            } else if (field == "zipcode") {
              problems.push(this.capitalize(this.$t('text.zip')));
            } else {
              problems.push(this.toSentenceCase(field));
            }
          }
        } else {
          if (field == "zipcode") {
            if (this.country == "United States" || this.country == "Canada") {
              // Validate US and Canadian zip codes
              // US: 12345 or 12345-6789
              // Canada: A1A 1A1 or A1A1A1
              const usZipcodeRegex = /^((\d{5}-\d{4})|(\d{5})|([A-Z]\d[A-Z]\s?\d[A-Z]\d))$/i;
              if (
                usZipcodeRegex.test(this.zipcode.trim()) === false
              ) {
                problems.push(this.capitalize(this.$t('text.zip')));
              } 
            } else if (this.country == "United Kingdom") {
              // Validate UK postcodes
              // UK: A1 1AA or A11 1AA
              const ukPostcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;
              if (
                ukPostcodeRegex.test(this.zipcode.trim()) === false
              ) {
                problems.push(this.capitalize(this.$t('text.zip')));
              }
            } else {
              // Other countries - length validation only
              if (this.zipcode.trim().length < 3) {
                problems.push(this.capitalize(this.$t('text.zip')));
              }
            }
          }
        }
      }

      if (
        !this.$methods.isIndependentUser() &&
        this.require_unique_identifier
      ) {
        if (this.uniqueIdentifier == null || this.uniqueIdentifier == "") {
          problems.push(this.$methods.getStudentWord(true) + " ID");
        }
      }

      if (!this.userBirthdate) {
        // Also validate birthdate
        if (this.birthDate == null) {
          problems.push("Birthdate");
        }
      }
      return problems;
    },
    birthDateErrorMessages() {
      this.birthDate;
      return this.requiredErrors("birthDate");
    },
    graduationDateErrorMessages() {
      this.graduationDate;
      if (this.skip_graduation_date) return [];
      return this.requiredErrors("graduationDate");
    },
    birthDateAndGraduationRule() {
      return checkyear =>  {
        return birthDate => {
          const invalidMessage = "Please enter a valid date. Example: 2000-01-15.";

          // Example: 2000-01-15
          // Year should have exactly 4 characters
          // Month and day should each have either 1 or 2 characters
          const birthDateRegex = /^\d{4}-\d{1,2}-\d{1,2}$/;
          if (!birthDateRegex.test(birthDate)) return invalidMessage;

          // At this point, birthDate has 2 dashes and only numbers

          // Split birth date to test for min and max of year, month, and day
          const splitBirthDate = birthDate.split("-");
          const birthYear = parseInt(splitBirthDate[0]);
          const birthMonth = parseInt(splitBirthDate[1]);
          const birthDay = parseInt(splitBirthDate[2]);

          // Year out of bounds
          if (checkyear) {
            const currentYear = new Date().getFullYear();
            if (birthYear < 1920 || birthYear > currentYear) return invalidMessage;
          } else {
            // If not checking year, we only allow years from 1920 onwards
            if (birthYear < 1920) return invalidMessage;
          }
          // Month out of bounds
          if (birthMonth < 1 || birthMonth > 12) return invalidMessage;

          // Day out of bounds
          if (birthDay < 1 || birthDay > 31) return invalidMessage;

          // At this point, birth date valid
          return true;
        };
      };
    },
  },
  watch: {
    birthDate(val) {
      // Replace all / with - while user is typing
      // Before replacement: 2020/01/15
      // After replacement: 2020-01-15
      const refinedBirthDate = val.replaceAll("/", "-");
      this.birthDate = refinedBirthDate;
    },
    graduationDate(val) {
      // Replace all / with - while user is typing
      // Before replacement: 2020/01/15
      // After replacement: 2020-01-15
      const refinedGraduationDate = val.replaceAll("/", "-");
      this.graduationDate = refinedGraduationDate;
    }
  },
  mounted: function() {
    this.telephone = this.userTelephone;
    // TODO: properly DRY up all axios calls to "/fieldvalues"
    // We should standardize the way we cache this data anyway
    this.$methods.getFieldValues().then(data => {
      const ethnicities = data.ethnicities.sort((a, b) => a.localeCompare(b));
      ethnicities.unshift("Prefer not to say");
      // Polish the text but not value (so API doesn't get confused)
      this.ethnicity_choices = ethnicities.map((ethnicity) => {
        let ethnicityText = ethnicity.replace("Or", "or"); // Black or African American
        return { value: ethnicity, text: ethnicityText }
      });

      this.reason_choices = data.reason_translations;
      this.pronoun_choices = data.pronouns;
      this.focus_area_choices = data.focus_areas.sort((a, b) =>
        a.localeCompare(b)
      );
      this.communities_groups_options = data.communities_groups;
      this.language_choices = data.languages;
      this.employer = data.employer;
      this.require_unique_identifier = data.require_unique_identifier;
    });
    window.scrollTo(0,0);
  },
  methods: {
    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },
    telephoneErrorMessages() {
      this.telephone;
      return this.telephoneErrors("telephone");
    },
    updateGraduationDate(event) {
      this.$emit("update:graduationDateValidated", event);
    },
    updateBirthDate(event) {
      this.$emit("update:birthDateValidated", event);
    },
    updateFocusAreas(event) {
      // Only allow selection of 3 focus areas
      const focusAreasLengthBeforeSlice = event.length;
      this.focusAreas = this.focusAreas.slice(0, 3);

      // Fixes a bug where component doesn't render if the checkbox itself is selected
      if (focusAreasLengthBeforeSlice > 3) this.forceRerenderFocusAreas();
    },
    uniqueIdentifierErrors() {
      if (this.v$.uniqueIdentifier != null && !this.v$.uniqueIdentifier.$dirty) {
        return [];
      }
      let errors = this.requiredErrors("uniqueIdentifier", false);
      !this.v$.uniqueIdentifier.minLength &&
        errors.push(this.$methods.getStudentWord(true) + " ID must be at least 4 characters long");    
        
      let emitValue = null;
      if (errors.length == 0) {
        emitValue = this.v$.uniqueIdentifier.$model;
      }
      this.$emit("update:uniqueIdentifierValidated", emitValue);
      return errors;
    },
    resendConfirmation: function() {
      new Promise(resolve => {
        axios({
          method: "post",
          url: "/resendconfirmation"
        }).then(resp => {
          window.Bus.$emit("flash-message", {
            text: resp.data.message
          });
          resolve();
        });
      });
    },
    finish: function(step) {
      // Make sure all the required fields are valid
      if (step == 1) {
        let problems = new Intl.ListFormat().format(this.basicFormProblems);
        window.Bus.$emit("flash-message", {
          text: "Please fill out all the required fields: " + problems,
          error: true
        });
      } else if (this.basicFormValid) {
        let json = localStorage.getItem("uwillFieldvalues");

        let fieldValues = JSON.parse(json);
        let focusAreas = this.focusAreas.map(function(item) {
          return fieldValues["focus_areas"].indexOf(item);
        });
        let ethnicity = this.ethnicity.map(function(item) {
          return fieldValues["ethnicities"].indexOf(item);
        });

        // Submit the form
        let form_attributes = {
          address1: this.address1,
          address2: this.address2,
          city: this.city,
          zipcode: this.zipcode,
          focus_areas: focusAreas,
          state: this.state,
          country: this.country,
          about: this.about,
          preferred_name: this.preferredName,
          gender: this.gender,
          telephone: this.telephone,
          graduation_date: this.graduationDate,
          reasons: this.reasons,
          pronouns: this.pronouns,
          contact_1_name: this.contact1Name,
          contact_1_telephone: this.contact1Telephone,
          contact_2_telephone: this.contact2Telephone,
          ethnicities: ethnicity,
          unique_identifier: this.uniqueIdentifier,
          communities_groups: this.communitiesGroups
        };
        if (this.birthDate) {
          form_attributes["birthdate"] = this.birthDate;
        }
        let formData = new FormData();
        Object.entries(form_attributes).forEach(([key, value]) =>
          formData.append(key, value)
        );
        axios
          .put("/user", formData, {})
          .then(resp => {
            window.Bus.$emit("flash-message", {
              text: resp.data.message
            });
            this.$store.dispatch("get_user");
          });
      }

    },
    capitalize: function(word, titleize = false) {
      if (titleize) {
        var words = word.split(" ");
        return words
          .map(word => {
            return word[0].toUpperCase() + word.substring(1);
          })
          .join(" ");
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
    }  
  }
};
</script>
<style>
.field-title {
  color: #6364BA;
  font-weight: 600;
  font-size: 14px; 
}
</style>
